import { useCallback, useState } from 'react';
import { currentRequestDates } from './requestDates';

const DRAFT_KEY = 'lovo:v2:request-draft';
const ROUTE_SEED_KEY = 'lovo:new-request-route';
const PHOTO_DB_NAME = 'lovo-request-wizard';
const PHOTO_STORE = 'pending-photos';

export type PetType = 'dog' | 'cat' | 'rodent' | 'bird' | 'farm' | 'other';
export type AccountType = 'person' | 'company';

export interface RequestDraft {
  fromCity: string;
  toCity: string;
  petType: PetType;
  customType: string;
  breed: string;
  count: number;
  weightKg: string;
  age: string;
  petName: string;
  pickupNotBefore: string;
  deliveryNotAfter: string;
  urgency: string;
  carrierBox: string;
  documentsStatus: string;
  petNotes: string;
  feedingNotes: string;
  pickupContactName: string;
  pickupContactPhone: string;
  pickupContactNotes: string;
  deliveryContactName: string;
  deliveryContactPhone: string;
  deliveryContactNotes: string;
  budgetMode: 'fixed' | 'offers';
  budgetMin: string;
  budgetMax: string;
  photoKeys: string[];
  photoAssetIds: number[];
  requestId: number | null;
  publicCode: string;
  email: string;
  accountType: AccountType;
  firstName: string;
  lastName: string;
  companyName: string;
  contactName: string;
  phone: string;
  smsCode: string;
  debugSmsCode: string;
}

export interface PendingPhoto {
  id: string;
  name: string;
  type: string;
  size: number;
  lastModified: number;
  blob: Blob;
}

const EMPTY_DRAFT: RequestDraft = {
  fromCity: '',
  toCity: '',
  petType: 'dog',
  customType: '',
  breed: '',
  count: 1,
  weightKg: '',
  age: '',
  petName: '',
  pickupNotBefore: '',
  deliveryNotAfter: '',
  urgency: '',
  carrierBox: 'own_box',
  documentsStatus: 'ready',
  petNotes: '',
  feedingNotes: 'owner_food',
  pickupContactName: '',
  pickupContactPhone: '',
  pickupContactNotes: '',
  deliveryContactName: '',
  deliveryContactPhone: '',
  deliveryContactNotes: '',
  budgetMode: 'fixed',
  budgetMin: '',
  budgetMax: '',
  photoKeys: [],
  photoAssetIds: [],
  requestId: null,
  publicCode: '',
  email: '',
  accountType: 'person',
  firstName: '',
  lastName: '',
  companyName: '',
  contactName: '',
  phone: '',
  smsCode: '',
  debugSmsCode: '',
};

function parseRouteSeed(): Pick<RequestDraft, 'fromCity' | 'toCity'> | null {
  try {
    const raw = sessionStorage.getItem(ROUTE_SEED_KEY) ?? localStorage.getItem(ROUTE_SEED_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as {
      from?: unknown;
      fromCity?: unknown;
      to?: unknown;
      toCity?: unknown;
    };
    const rawFrom = typeof value.fromCity === 'string' ? value.fromCity : value.from;
    const rawTo = typeof value.toCity === 'string' ? value.toCity : value.to;
    const fromCity = typeof rawFrom === 'string' ? rawFrom.trim() : '';
    const toCity = typeof rawTo === 'string' ? rawTo.trim() : '';
    sessionStorage.removeItem(ROUTE_SEED_KEY);
    localStorage.removeItem(ROUTE_SEED_KEY);
    return fromCity || toCity ? { fromCity, toCity } : null;
  } catch {
    sessionStorage.removeItem(ROUTE_SEED_KEY);
    localStorage.removeItem(ROUTE_SEED_KEY);
    return null;
  }
}

export function readRequestDraft(): RequestDraft {
  let stored: Partial<RequestDraft> = {};
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (raw) stored = JSON.parse(raw) as Partial<RequestDraft>;
  } catch {
    localStorage.removeItem(DRAFT_KEY);
  }
  // Published requests are completed records, not reusable wizard drafts.
  // This also clears stale completed drafts left by earlier frontend versions.
  if (stored.requestId && stored.publicCode) {
    localStorage.removeItem(DRAFT_KEY);
    stored = {};
  }
  const seeded = parseRouteSeed();
  const draft = { ...EMPTY_DRAFT, ...stored, ...(seeded ?? {}) };
  const dates = currentRequestDates(draft);
  const datesChanged = dates.pickupNotBefore !== draft.pickupNotBefore
    || dates.deliveryNotAfter !== draft.deliveryNotAfter;
  Object.assign(draft, dates);
  if (seeded || datesChanged) localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  return draft;
}

export function saveRequestDraft(draft: RequestDraft): void {
  localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
}

export function clearRequestDraft(): void {
  localStorage.removeItem(DRAFT_KEY);
}

export function useRequestDraft() {
  const [draft, setDraft] = useState<RequestDraft>(readRequestDraft);

  const updateDraft = useCallback((patch: Partial<RequestDraft>) => {
    setDraft((current) => {
      const next = { ...current, ...patch };
      saveRequestDraft(next);
      return next;
    });
  }, []);

  const replaceDraft = useCallback((next: RequestDraft) => {
    saveRequestDraft(next);
    setDraft(next);
  }, []);

  const completeDraft = useCallback((patch: Partial<RequestDraft>) => {
    clearRequestDraft();
    setDraft((current) => ({ ...current, ...patch }));
  }, []);

  return { completeDraft, draft, replaceDraft, updateDraft };
}

function openPhotoDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(PHOTO_DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(PHOTO_STORE)) {
        request.result.createObjectStore(PHOTO_STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Не удалось открыть хранилище фото'));
  });
}

function photoTransaction<T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore, resolve: (value: T) => void, reject: (reason?: unknown) => void) => void,
): Promise<T> {
  return openPhotoDatabase().then(
    (database) =>
      new Promise<T>((resolve, reject) => {
        const transaction = database.transaction(PHOTO_STORE, mode);
        transaction.oncomplete = () => database.close();
        transaction.onerror = () => reject(transaction.error);
        action(transaction.objectStore(PHOTO_STORE), resolve, reject);
      }),
  );
}

function pendingPhotoId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();

  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
}

export async function storePendingPhoto(file: File): Promise<PendingPhoto> {
  const photo: PendingPhoto = {
    id: pendingPhotoId(),
    name: file.name,
    type: file.type,
    size: file.size,
    lastModified: file.lastModified,
    blob: file,
  };
  await photoTransaction<void>('readwrite', (store, resolve, reject) => {
    const request = store.put(photo);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
  return photo;
}

export async function getPendingPhotos(ids: string[]): Promise<PendingPhoto[]> {
  const photos = await Promise.all(
    ids.map((id) =>
      photoTransaction<PendingPhoto | null>('readonly', (store, resolve, reject) => {
        const request = store.get(id);
        request.onsuccess = () => resolve((request.result as PendingPhoto | undefined) ?? null);
        request.onerror = () => reject(request.error);
      }),
    ),
  );
  return photos.filter((photo): photo is PendingPhoto => photo !== null);
}

export async function removePendingPhoto(id: string): Promise<void> {
  await photoTransaction<void>('readwrite', (store, resolve, reject) => {
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function clearPendingPhotos(ids: string[]): Promise<void> {
  await Promise.all(ids.map(removePendingPhoto));
}

export function emptyRequestDraft(): RequestDraft {
  return { ...EMPTY_DRAFT, ...currentRequestDates(EMPTY_DRAFT) };
}
